import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, MapPin, UsersRound, CalendarDays,
  Briefcase, TrendingUp, AlertTriangle, CheckCircle2,
  Clock, ChevronRight, Plus
} from 'lucide-react';
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('mideast_token')}` }
});

const DASH_STYLES = `
  .md-dash-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 28px;
  }
  @media (max-width: 1200px) {
    .md-dash-stats { grid-template-columns: repeat(2, 1fr); }
  }

  .md-stat-card {
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 14px;
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: border-color 0.2s, transform 0.2s;
    cursor: default;
  }
  .md-stat-card:hover {
    border-color: rgba(14,165,233,0.20);
    transform: translateY(-2px);
  }
  .md-stat-card-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
  }
  .md-stat-icon {
    width: 40px; height: 40px;
    border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .md-stat-icon-blue   { background: rgba(14,165,233,0.12);  color: #38BDF8; }
  .md-stat-icon-gold   { background: rgba(245,158,11,0.12);  color: #FCD34D; }
  .md-stat-icon-green  { background: rgba(34,197,94,0.12);   color: #86EFAC; }
  .md-stat-icon-purple { background: rgba(167,139,250,0.12); color: #C4B5FD; }
  .md-stat-icon-red    { background: rgba(239,68,68,0.12);   color: #FCA5A5; }

  .md-stat-delta {
    font-size: 10px; font-weight: 700;
    font-family: 'DM Mono', monospace;
    padding: 3px 8px; border-radius: 20px;
  }
  .md-stat-delta-up   { background: rgba(34,197,94,0.10); color: #86EFAC; }
  .md-stat-delta-zero { background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.30); }

  .md-stat-value {
    font-size: 36px; font-weight: 800;
    color: #F1F5F9; letter-spacing: -0.04em; line-height: 1;
  }
  .md-stat-label {
    font-size: 10px; font-weight: 700;
    letter-spacing: 0.14em; text-transform: uppercase;
    color: rgba(255,255,255,0.28);
    font-family: 'DM Mono', monospace;
  }
  .md-stat-sub {
    font-size: 11px; color: rgba(255,255,255,0.22);
  }

  /* Bottom row layout */
  .md-dash-bottom {
    display: grid;
    grid-template-columns: 1fr 340px;
    gap: 20px;
    align-items: start;
  }
  @media (max-width: 1100px) {
    .md-dash-bottom { grid-template-columns: 1fr; }
  }

  /* Status badge in table */
  .md-status {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 3px 10px; border-radius: 20px;
    font-size: 10px; font-weight: 700;
    font-family: 'DM Mono', monospace;
    letter-spacing: 0.06em;
  }
  .md-status-dot {
    width: 5px; height: 5px; border-radius: 50%;
  }
  .md-status-scheduled  { background: rgba(14,165,233,0.12);  color: #38BDF8; }
  .md-status-scheduled .md-status-dot  { background: #38BDF8; }
  .md-status-in_progress{ background: rgba(245,158,11,0.12);  color: #FCD34D; }
  .md-status-in_progress .md-status-dot{ background: #FCD34D; }
  .md-status-completed  { background: rgba(34,197,94,0.12);   color: #86EFAC; }
  .md-status-completed .md-status-dot  { background: #86EFAC; }
  .md-status-cancelled  { background: rgba(239,68,68,0.10);   color: #FCA5A5; }
  .md-status-cancelled .md-status-dot  { background: #FCA5A5; }

  /* Quick actions panel */
  .md-quick-actions {
    display: flex; flex-direction: column; gap: 10px;
  }
  .md-quick-action-btn {
    display: flex; align-items: center; gap: 14px;
    padding: 16px 18px;
    background: #0D1120;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.18s ease;
    text-decoration: none;
  }
  .md-quick-action-btn:hover {
    border-color: rgba(14,165,233,0.22);
    background: rgba(14,165,233,0.04);
    transform: translateX(3px);
  }
  .md-quick-action-icon {
    width: 36px; height: 36px; border-radius: 10px;
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }
  .md-quick-action-text { flex: 1; text-align: left; }
  .md-quick-action-title {
    font-size: 13px; font-weight: 700;
    color: rgba(255,255,255,0.75);
    display: block; margin-bottom: 2px;
  }
  .md-quick-action-sub {
    font-size: 11px; color: rgba(255,255,255,0.25);
  }
  .md-quick-action-arrow {
    color: rgba(255,255,255,0.18);
    flex-shrink: 0;
  }

  /* Page header */
  .md-page-header {
    display: flex; align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 28px;
    gap: 16px;
  }
  .md-page-title {
    font-size: 22px; font-weight: 800;
    color: #F1F5F9; letter-spacing: -0.03em;
    margin-bottom: 4px;
  }
  .md-page-sub {
    font-size: 12px; color: rgba(255,255,255,0.28);
  }

  /* Today strip */
  .md-today-strip {
    background: linear-gradient(135deg, rgba(14,165,233,0.08), rgba(245,158,11,0.05));
    border: 1px solid rgba(14,165,233,0.14);
    border-radius: 12px;
    padding: 14px 18px;
    display: flex; align-items: center; gap: 12px;
    margin-bottom: 20px;
  }
  .md-today-dot {
    width: 8px; height: 8px; border-radius: 50%;
    background: #0EA5E9; flex-shrink: 0;
    box-shadow: 0 0 8px rgba(14,165,233,0.70);
    animation: mdPulse 2s ease-in-out infinite;
  }
  @keyframes mdPulse {
    0%,100%{ box-shadow: 0 0 6px rgba(14,165,233,0.60); }
    50%    { box-shadow: 0 0 14px rgba(14,165,233,0.95); }
  }
  .md-today-text {
    font-size: 12px; color: rgba(255,255,255,0.45);
    font-family: 'DM Mono', monospace;
  }
  .md-today-text strong { color: rgba(14,165,233,0.80); }

  /* Skeleton loader */
  .md-skeleton {
    background: linear-gradient(90deg,
      rgba(255,255,255,0.04) 0%,
      rgba(255,255,255,0.08) 50%,
      rgba(255,255,255,0.04) 100%);
    background-size: 200% 100%;
    animation: mdSkeleton 1.4s ease-in-out infinite;
    border-radius: 6px;
  }
  @keyframes mdSkeleton {
    0%  { background-position: 200% 0; }
    100%{ background-position: -200% 0; }
  }

  /* Flag summary row */
  .md-flag-row {
    display: flex; gap: 10px; flex-wrap: wrap;
    margin-bottom: 20px;
  }
  .md-flag-chip {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 7px 14px; border-radius: 20px;
    font-size: 11px; font-weight: 700;
    font-family: 'DM Mono', monospace;
    border: 1px solid;
  }
  .md-flag-chip-gold  { background: rgba(245,158,11,0.08); color: #FCD34D; border-color: rgba(245,158,11,0.20); }
  .md-flag-chip-red   { background: rgba(239,68,68,0.08);  color: #FCA5A5; border-color: rgba(239,68,68,0.20); }
  .md-flag-chip-blue  { background: rgba(14,165,233,0.08); color: #38BDF8; border-color: rgba(14,165,233,0.20); }
`;

const StatusBadge = ({ status }) => (
  <span className={`md-status md-status-${status}`}>
    <span className="md-status-dot"/>
    {status.replace('_', ' ')}
  </span>
);

const StatCard = ({ icon: Icon, iconClass, label, value, sub, delta, loading }) => (
  <div className="md-stat-card">
    <div className="md-stat-card-top">
      <div className={`md-stat-icon ${iconClass}`}>
        <Icon size={18} strokeWidth={1.8}/>
      </div>
      {delta !== undefined && (
        <span className={`md-stat-delta ${delta > 0 ? 'md-stat-delta-up' : 'md-stat-delta-zero'}`}>
          {delta > 0 ? `+${delta}` : delta}
        </span>
      )}
    </div>
    {loading
      ? <div className="md-skeleton" style={{ height: 36, width: '60%' }}/>
      : <div className="md-stat-value">{value ?? '—'}</div>
    }
    <div>
      <div className="md-stat-label">{label}</div>
      {sub && <div className="md-stat-sub">{sub}</div>}
    </div>
  </div>
);

const MideastDashboard = () => {
  const navigate = useNavigate();
  const [stats,      setStats]      = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [jobsLoading,setJobsLoading]= useState(true);

  const today = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
  });

  const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM

  const fetchStats = useCallback(async () => {
    try {
      const [clientsRes, sitesRes, teamsRes, jobsRes] = await Promise.all([
        axios.get(`${BASE}/mideast/clients`,                              getAuthHeader()),
        axios.get(`${BASE}/mideast/sites`,                                getAuthHeader()),
        axios.get(`${BASE}/mideast/teams`,                                getAuthHeader()),
        axios.get(`${BASE}/mideast/jobs?month=${currentMonth}`,           getAuthHeader()),
      ]);

      const jobs      = jobsRes.data.jobs      || [];
      const scheduled = jobs.filter(j => j.status === 'scheduled').length;
      const completed = jobs.filter(j => j.status === 'completed').length;
      const inProg    = jobs.filter(j => j.status === 'in_progress').length;

      setStats({
        clients:    (clientsRes.data.clients || []).length,
        sites:      (sitesRes.data.sites     || []).length,
        teams:      (teamsRes.data.teams     || []).length,
        jobsMonth:  jobs.length,
        scheduled,
        completed,
        inProg,
      });
    } catch (err) {
      console.error('[Dashboard] Stats error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentMonth]);

  const fetchRecentJobs = useCallback(async () => {
    try {
      const res = await axios.get(
        `${BASE}/mideast/jobs?month=${currentMonth}`,
        getAuthHeader()
      );
      // Show latest 8
      const sorted = (res.data.jobs || [])
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 8);
      setRecentJobs(sorted);
    } catch (err) {
      console.error('[Dashboard] Jobs error:', err);
    } finally {
      setJobsLoading(false);
    }
  }, [currentMonth]);

  useEffect(() => {
    fetchStats();
    fetchRecentJobs();
  }, [fetchStats, fetchRecentJobs]);

  const QUICK_ACTIONS = [
    {
      title: 'Add New Client',
      sub:   'Register a Mideast client',
      icon:  Users,
      iconBg:'rgba(14,165,233,0.12)',
      iconColor: '#38BDF8',
      path:  '/me/admin/clients',
    },
    {
      title: 'Add Site',
      sub:   'Register a new solar site',
      icon:  MapPin,
      iconBg:'rgba(245,158,11,0.12)',
      iconColor: '#FCD34D',
      path:  '/me/admin/sites',
    },
    {
      title: 'Schedule a Job',
      sub:   'Assign team to a site',
      icon:  CalendarDays,
      iconBg:'rgba(34,197,94,0.12)',
      iconColor: '#86EFAC',
      path:  '/me/admin/schedule',
    },
    {
      title: 'Manage Teams',
      sub:   'Add leads and members',
      icon:  UsersRound,
      iconBg:'rgba(167,139,250,0.12)',
      iconColor: '#C4B5FD',
      path:  '/me/admin/teams',
    },
  ];

  return (
    <>
      <style>{DASH_STYLES}</style>

      {/* Page header */}
      <div className="md-page-header">
        <div>
          <div className="md-page-title">Overview</div>
          <div className="md-page-sub">Mideast operations at a glance</div>
        </div>
        <button
          className="md-btn md-btn-primary"
          onClick={() => navigate('/me/admin/schedule')}
        >
          <Plus size={14}/> Schedule Job
        </button>
      </div>

      {/* Today strip */}
      <div className="md-today-strip">
        <div className="md-today-dot"/>
        <div className="md-today-text">
          <strong>Today</strong> — {today}
        </div>
        {stats && (
          <div className="md-today-text" style={{ marginLeft: 'auto' }}>
            <strong>{stats.inProg}</strong> job{stats.inProg !== 1 ? 's' : ''} in progress
            &nbsp;·&nbsp;
            <strong>{stats.scheduled}</strong> scheduled this month
          </div>
        )}
      </div>

      {/* Stats row */}
      <div className="md-dash-stats">
        <StatCard
          icon={Users} iconClass="md-stat-icon-blue"
          label="Total Clients" value={stats?.clients}
          sub="Active Mideast accounts" loading={loading}
        />
        <StatCard
          icon={MapPin} iconClass="md-stat-icon-gold"
          label="Total Sites" value={stats?.sites}
          sub="Registered solar sites" loading={loading}
        />
        <StatCard
          icon={UsersRound} iconClass="md-stat-icon-green"
          label="Active Teams" value={stats?.teams}
          sub="Field operation teams" loading={loading}
        />
        <StatCard
          icon={CalendarDays} iconClass="md-stat-icon-purple"
          label="Jobs This Month" value={stats?.jobsMonth}
          sub={`${stats?.completed ?? '—'} completed`} loading={loading}
        />
      </div>

      {/* Flag summary */}
      {stats && (
        <div className="md-flag-row">
          <div className="md-flag-chip md-flag-chip-blue">
            <Clock size={11}/> {stats.scheduled} Scheduled
          </div>
          <div className="md-flag-chip md-flag-chip-gold">
            <TrendingUp size={11}/> {stats.inProg} In Progress
          </div>
          <div className="md-flag-chip md-flag-chip-blue" style={{
            background: 'rgba(34,197,94,0.08)',
            color: '#86EFAC',
            borderColor: 'rgba(34,197,94,0.20)'
          }}>
            <CheckCircle2 size={11}/> {stats.completed} Completed
          </div>
        </div>
      )}

      {/* Bottom grid */}
      <div className="md-dash-bottom">

        {/* Recent jobs */}
        <div className="md-card">
          <div className="md-card-header">
            <Briefcase size={15} color="rgba(14,165,233,0.70)" strokeWidth={1.8}/>
            <div className="md-card-title">Recent Jobs</div>
            <button
              className="md-btn md-btn-ghost"
              style={{ padding: '5px 12px', fontSize: 11 }}
              onClick={() => navigate('/me/admin/jobs')}
            >
              View All <ChevronRight size={12}/>
            </button>
          </div>

          {jobsLoading ? (
            <div className="md-card-body">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="md-skeleton"
                  style={{ height: 18, marginBottom: 14, width: `${70 + Math.random() * 25}%` }}/>
              ))}
            </div>
          ) : recentJobs.length === 0 ? (
            <div className="md-empty">
              <div className="md-empty-icon"><Briefcase size={28}/></div>
              No jobs scheduled this month
            </div>
          ) : (
            <table className="md-table">
              <thead>
                <tr>
                  <th>Site</th>
                  <th>Client</th>
                  <th>Team</th>
                  <th>Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentJobs.map(job => (
                  <tr
                    key={job.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/me/admin/jobs')}
                  >
                    <td style={{ fontWeight: 600, color: '#E2E8F0' }}>
                      {job.site_name}
                    </td>
                    <td style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                      {job.client_name}
                    </td>
                    <td style={{ color: 'rgba(255,255,255,0.45)', fontSize: 12 }}>
                      {job.team_name || <span style={{ opacity: 0.30 }}>Unassigned</span>}
                    </td>
                    <td style={{ fontFamily: 'DM Mono', fontSize: 11, color: 'rgba(255,255,255,0.40)' }}>
                      {new Date(job.scheduled_date).toLocaleDateString('en-GB', {
                        day: 'numeric', month: 'short'
                      })}
                    </td>
                    <td><StatusBadge status={job.status}/></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Quick actions */}
        <div>
          <div className="md-card-title" style={{ marginBottom: 14, fontSize: 13, color: 'rgba(255,255,255,0.40)', fontFamily: 'DM Mono', letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 10 }}>
            Quick Actions
          </div>
          <div className="md-quick-actions">
            {QUICK_ACTIONS.map(({ title, sub, icon: Icon, iconBg, iconColor, path }) => (
              <button
                key={path}
                className="md-quick-action-btn"
                onClick={() => navigate(path)}
              >
                <div className="md-quick-action-icon" style={{ background: iconBg }}>
                  <Icon size={16} color={iconColor} strokeWidth={1.8}/>
                </div>
                <div className="md-quick-action-text">
                  <span className="md-quick-action-title">{title}</span>
                  <span className="md-quick-action-sub">{sub}</span>
                </div>
                <ChevronRight size={14} className="md-quick-action-arrow"/>
              </button>
            ))}
          </div>
        </div>

      </div>
    </>
  );
};

export default MideastDashboard;